import { ArrowUpRight, Bot, Mail, MessageCircle, Send, Sparkles, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const VEHICLE_MARKER = /\n?\s*FAHRZEUGE:[0-9,\s]*$/i;

function getSessionId() {
  if (typeof window === "undefined") return "moselcars-server-session";
  const stored = window.sessionStorage.getItem("moselcars-ai-session");
  if (stored) return stored;
  const next = window.crypto?.randomUUID?.() || `moselcars-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  window.sessionStorage.setItem("moselcars-ai-session", next);
  return next;
}

function findMentionedVehicles(text, vehicles) {
  const normalized = text.toLowerCase();
  return vehicles.filter((vehicle) => normalized.includes(`${vehicle.brand} ${vehicle.title}`.toLowerCase()) || normalized.includes(vehicle.title.toLowerCase()));
}

function displayContent(content) {
  return content.replace(VEHICLE_MARKER, "").trimEnd();
}

function AiVehicleCard({ vehicle, onOpenVehicle, onPrepareInquiry }) {
  return (
    <article className="ai-vehicle-card" data-testid={`ai-vehicle-card-${vehicle.id}`}>
      <button className="ai-vehicle-image" type="button" onClick={() => onOpenVehicle(vehicle)} aria-label={`${vehicle.brand} ${vehicle.title} ansehen`} data-testid={`ai-vehicle-image-${vehicle.id}`}>
        <img src={vehicle.image} alt={`${vehicle.brand} ${vehicle.title}`} loading="lazy" />
        {vehicle.images?.length > 1 && <span>{vehicle.images.length} Fotos</span>}
      </button>
      <div className="ai-vehicle-copy">
        <span className="mono-label">{vehicle.brand} · {vehicle.year}</span>
        <h3>{vehicle.title}</h3>
        <div className="ai-vehicle-meta"><strong>{vehicle.price}</strong><span>{vehicle.mileage}</span><span>{vehicle.fuel}</span><span>{vehicle.gearbox}</span></div>
        <div className="ai-vehicle-actions">
          <button className="text-action" type="button" onClick={() => onOpenVehicle(vehicle)} data-testid={`ai-vehicle-details-${vehicle.id}`}>Details ansehen <ArrowUpRight size={14} /></button>
          <button className="text-action" type="button" onClick={() => onPrepareInquiry(vehicle)} data-testid={`ai-prepare-inquiry-${vehicle.id}`}>Anfrage vorbereiten <Mail size={14} /></button>
        </div>
      </div>
    </article>
  );
}

export default function AiAdvisor({ vehicles, selectedVehicles, onPrepareInquiry, onOpenVehicle }) {
  const [isOpen, setIsOpen] = useState(false);
  const [sessionId] = useState(getSessionId);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([{ role: "assistant", content: "Hallo, ich helfe dir beim Finden eines passenden Gebrauchtwagens. Frage mich nach Fahrzeugen, Besichtigung, Standort oder Finanzierung.", vehicles: [] }]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState("");
  const messageListRef = useRef(null);

  const quickPrompts = useMemo(() => selectedVehicles.length === 2
    ? ["Vergleiche meine Auswahl", "Welche Fahrzeuge passen unter 5.000 €?", "Wie läuft eine Besichtigung ab?"]
    : ["Welche Fahrzeuge passen unter 5.000 €?", "Welches Auto hat Automatik?", "Welches Fahrzeug ist für die Familie geeignet?"], [selectedVehicles.length]);

  useEffect(() => {
    const list = messageListRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages, isStreaming]);

  const updateLastAssistant = (updater) => setMessages((current) => {
    const next = [...current];
    const last = next[next.length - 1];
    if (last?.role === "assistant") next[next.length - 1] = updater(last);
    return next;
  });

  const sendMessage = async (preset) => {
    const message = String(preset || input).trim();
    if (!message || isStreaming) return;
    setInput("");
    setError("");
    setMessages((current) => [...current, { role: "user", content: message, vehicles: [] }, { role: "assistant", content: "", vehicles: [] }]);
    setIsStreaming(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
        body: JSON.stringify({ session_id: sessionId, message, vehicle_ids: selectedVehicles.map((vehicle) => vehicle.id) })
      });
      if (!response.ok || !response.body) throw new Error("Der Fahrzeugberater ist gerade nicht erreichbar.");
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let answer = "";
      let suggestedIds = [];
      let finished = false;

      while (!finished) {
        const { value, done } = await reader.read();
        buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        for (const event of events) {
          const line = event.split("\n").find((entry) => entry.startsWith("data: "));
          if (!line) continue;
          const payload = JSON.parse(line.slice(6));
          if (payload.type === "token") {
            answer += payload.content;
            updateLastAssistant((last) => ({ ...last, content: `${last.content}${payload.content}` }));
          }
          if (payload.type === "error") throw new Error(payload.message);
          if (payload.type === "done") {
            suggestedIds = Array.isArray(payload.suggested_vehicle_ids) ? payload.suggested_vehicle_ids : [];
            finished = true;
          }
        }
        if (done) finished = true;
      }

      const byId = suggestedIds.map((id) => vehicles.find((vehicle) => vehicle.id === id)).filter(Boolean);
      const matched = byId.length ? byId : findMentionedVehicles(displayContent(answer), vehicles);
      updateLastAssistant((last) => ({ ...last, content: displayContent(last.content), vehicles: matched.slice(0, 3) }));
    } catch (requestError) {
      setError(requestError.message || "Der Fahrzeugberater ist gerade nicht erreichbar.");
      setMessages((current) => current.slice(0, -1));
    } finally {
      setIsStreaming(false);
    }
  };

  const openAdvisor = () => {
    setIsOpen(true);
    window.requestAnimationFrame(() => document.getElementById("berater")?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };

  return <>
    <section className="ai-advisor-section" id="berater" aria-labelledby="ai-advisor-title" data-testid="ai-advisor-section">
      <div className="page-shell">
        <div className="ai-advisor-intro"><div><span className="section-kicker">FAHRZEUGBERATER</span><h2 id="ai-advisor-title">Finden statt<br /><em>lange suchen.</em></h2></div><div><p>Frag Mosel Cars nach passenden Fahrzeugen, zwei Angeboten im Vergleich oder dem Ablauf einer Besichtigung. Der Berater nutzt den aktuellen Bestand, zeigt dir passende Fahrzeuge mit Foto und bereitet auf Wunsch eine Anfrage vor.</p><button className="button button-primary" type="button" onClick={() => setIsOpen((current) => !current)} data-testid="ai-advisor-open-button">{isOpen ? "Berater schließen" : "Berater öffnen"} <MessageCircle size={16} /></button></div></div>
        {isOpen && <div className="ai-chat-panel" data-testid="ai-chat-panel">
          <div className="ai-chat-header"><div><span className="ai-status-dot" /><strong>Mosel Cars Fahrzeugberater</strong><small>Antwortet auf Deutsch · Live-Bestand</small></div><Bot size={21} /></div>
          <div className="ai-quick-prompts">{quickPrompts.map((prompt) => <button type="button" key={prompt} onClick={() => sendMessage(prompt)} disabled={isStreaming} data-testid={`ai-quick-prompt-${quickPrompts.indexOf(prompt) + 1}`}>{prompt}</button>)}</div>
          <div className="ai-messages" aria-live="polite" ref={messageListRef} data-testid="ai-message-list">
            {messages.map((message, index) => <div className={`ai-message-block ai-message-block-${message.role}`} key={`${message.role}-${index}`}>
              <div className={`ai-message ai-message-${message.role}`} data-testid={`ai-message-${message.role}-${index + 1}`}><span>{message.role === "assistant" ? <Bot size={14} /> : "Du"}</span><p>{displayContent(message.content) || (isStreaming && index === messages.length - 1 ? "…" : "")}</p></div>
              {message.vehicles.length > 0 && <div className="ai-vehicle-cards" data-testid={`ai-vehicle-cards-${index + 1}`}>{message.vehicles.map((vehicle) => <AiVehicleCard vehicle={vehicle} key={vehicle.id} onOpenVehicle={onOpenVehicle} onPrepareInquiry={onPrepareInquiry} />)}</div>}
            </div>)}
          </div>
          {error && <p className="ai-error" role="alert" data-testid="ai-error-message">{error}</p>}
          <form className="ai-chat-form" onSubmit={(event) => { event.preventDefault(); sendMessage(); }}><label htmlFor="ai-question">Deine Frage<input id="ai-question" value={input} onChange={(event) => setInput(event.target.value)} placeholder="z. B. Welcher Wagen passt zu mir?" maxLength={2000} disabled={isStreaming} data-testid="ai-question-input" /></label><button className="button button-primary" type="submit" disabled={isStreaming || !input.trim()} aria-label="Frage senden" data-testid="ai-send-button"><Send size={16} /></button></form>
        </div>}
      </div>
    </section>
    <button className={`ai-chat-launcher ${isOpen ? "is-open" : ""}`} type="button" onClick={() => isOpen ? setIsOpen(false) : openAdvisor()} aria-label={isOpen ? "Fahrzeugberater schließen" : "Fahrzeugberater öffnen"} aria-expanded={isOpen} data-testid="ai-chat-launcher">{isOpen ? <X size={21} /> : <Sparkles size={21} />}</button>
  </>;
}
